import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, Clock, Play, Save, CheckCircle, AlertCircle, Timer, Eye, X, ChevronUp, ChevronDown, Zap, Shield, FlaskConical } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RoundScheduler() {
  const [rounds, setRounds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [triggeringRound, setTriggeringRound] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLeague, setPreviewLeague] = useState('racing');
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [expandedPlayer, setExpandedPlayer] = useState(null);

  useEffect(() => {
    fetchSchedule();
  }, []);

  const fetchSchedule = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/round-schedule`, { withCredentials: true });
      const existing = res.data;

      const allRounds = [];
      for (let i = 1; i <= 5; i++) {
        const found = existing.find(r => r.round_number === i);
        if (found) {
          allRounds.push({
            round_number: i,
            round_name: found.round_name || `Round ${i}`,
            scheduled_date: found.scheduled_datetime ? found.scheduled_datetime.split('T')[0] : '',
            scheduled_time: found.scheduled_datetime ? found.scheduled_datetime.split('T')[1]?.substring(0, 5) : '',
            heads_up_seconds: found.heads_up_seconds || 60,
            duration_seconds: found.duration_seconds || 300,
            league: found.league || 'both',
            round_type: found.round_type || 'official',
            status: found.status || 'scheduled'
          });
        } else {
          allRounds.push({
            round_number: i, round_name: `Round ${i}`,
            scheduled_date: '', scheduled_time: '',
            heads_up_seconds: 60, duration_seconds: 300,
            league: 'both', round_type: 'official', status: 'scheduled'
          });
        }
      }
      setRounds(allRounds);
    } catch (error) {
      console.error('Failed to fetch schedule:', error);
      toast.error('Failed to load round schedule');
    }
  };

  const updateRound = (index, field, value) => {
    setRounds(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const saveSchedule = async () => {
    setSaving(true);
    try {
      const payload = rounds.map(r => ({
        round_number: r.round_number, round_name: r.round_name,
        scheduled_datetime: r.scheduled_date && r.scheduled_time
          ? `${r.scheduled_date}T${r.scheduled_time}:00+00:00` : null,
        heads_up_seconds: r.heads_up_seconds, duration_seconds: r.duration_seconds,
        league: r.league, round_type: r.round_type, status: r.status
      }));
      await axios.post(`${BACKEND_URL}/api/admin/round-schedule`, { rounds: payload }, { withCredentials: true });
      toast.success('Round schedule saved successfully!');
      fetchSchedule();
    } catch (error) {
      toast.error('Failed to save schedule');
    } finally {
      setSaving(false);
    }
  };

  const triggerRound = async (roundNumber) => {
    if (!window.confirm(`Start Round ${roundNumber} now? This will lock player selections.`)) return;
    setTriggeringRound(roundNumber);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/round-schedule/${roundNumber}/trigger`, {}, { withCredentials: true });
      toast.success(`Round ${roundNumber} triggered successfully!`);
      fetchSchedule();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to trigger round');
    } finally {
      setTriggeringRound(null);
    }
  };

  const openPreview = async (league) => {
    setPreviewLeague(league);
    setPreviewOpen(true);
    setPreviewLoading(true);
    setExpandedPlayer(null);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/round-preview?league=${league}`, { withCredentials: true });
      setPreviewData(res.data);
    } catch (error) {
      toast.error('Failed to load preview');
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const switchPreviewLeague = async (league) => {
    setPreviewLeague(league);
    setPreviewLoading(true);
    setExpandedPlayer(null);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/round-preview?league=${league}`, { withCredentials: true });
      setPreviewData(res.data);
    } catch (error) {
      toast.error('Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      scheduled: 'bg-blue-900/40 text-blue-400 border-blue-700',
      triggered: 'bg-green-900/40 text-green-400 border-green-700',
      completed: 'bg-zinc-800 text-zinc-400 border-zinc-600',
    };
    const icons = {
      scheduled: <Timer size={12} />,
      triggered: <CheckCircle size={12} />,
      completed: <CheckCircle size={12} />,
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono border ${styles[status] || styles.scheduled}`}>
        {icons[status] || <AlertCircle size={12} />}
        {status?.toUpperCase() || 'SCHEDULED'}
      </span>
    );
  };

  return (
    <div className="space-y-6" data-testid="round-scheduler">
      <div className="bg-card border border-zinc-800 p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="font-heading font-bold text-2xl flex items-center">
              <Calendar className="mr-2 text-blue-500" size={24} />
              Round Schedule
            </h2>
            <p className="text-zinc-400 text-sm mt-1">
              Schedule rounds with auto-start or trigger them manually
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => openPreview('racing')}
              className="flex items-center gap-2 bg-purple-700 hover:bg-purple-600 text-white px-5 py-2 font-bold transition-colors"
              data-testid="preview-leaderboard-btn"
            >
              <Eye size={18} />
              <span>Preview Leaderboard</span>
            </button>
            <button
              onClick={saveSchedule}
              disabled={saving}
              className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-500 text-white px-6 py-2 font-bold transition-colors disabled:opacity-50"
              data-testid="save-schedule-btn"
            >
              <Save size={18} />
              <span>{saving ? 'Saving...' : 'Save Schedule'}</span>
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {rounds.map((round, index) => (
            <div
              key={round.round_number}
              className={`border p-5 space-y-4 ${
                round.status === 'triggered' ? 'border-green-800 bg-green-950/20' :
                round.status === 'completed' ? 'border-zinc-700 bg-zinc-900/50' :
                'border-zinc-700 bg-zinc-900/30'
              }`}
              data-testid={`round-${round.round_number}-card`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl font-heading font-bold text-primary">R{round.round_number}</span>
                  <input
                    type="text"
                    value={round.round_name}
                    onChange={(e) => updateRound(index, 'round_name', e.target.value)}
                    className="bg-transparent border-b border-zinc-700 px-2 py-1 text-white font-medium focus:border-primary outline-none"
                    placeholder={`Round ${round.round_number}`}
                    data-testid={`round-${round.round_number}-name`}
                  />
                  {getStatusBadge(round.status)}
                </div>
                <button
                  onClick={() => triggerRound(round.round_number)}
                  disabled={triggeringRound === round.round_number || round.status === 'triggered' || round.status === 'completed'}
                  className="flex items-center gap-2 bg-green-700 hover:bg-green-600 disabled:bg-zinc-700 disabled:text-zinc-500 text-white px-4 py-2 font-bold text-sm transition-colors"
                  data-testid={`round-${round.round_number}-trigger`}
                >
                  <Play size={14} />
                  {triggeringRound === round.round_number ? 'Starting...' : 'Start Now'}
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div>
                  <label className="block text-xs text-zinc-500 mb-1 font-mono">DATE</label>
                  <div className="relative">
                    <Calendar size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
                    <input type="date" value={round.scheduled_date}
                      onChange={(e) => updateRound(index, 'scheduled_date', e.target.value)}
                      className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2 pl-8 text-white text-sm"
                      data-testid={`round-${round.round_number}-date`} />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 mb-1 font-mono">TIME (UTC)</label>
                  <div className="relative">
                    <Clock size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
                    <input type="time" value={round.scheduled_time}
                      onChange={(e) => updateRound(index, 'scheduled_time', e.target.value)}
                      className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2 pl-8 text-white text-sm"
                      data-testid={`round-${round.round_number}-time`} />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 mb-1 font-mono">HEADS-UP (sec)</label>
                  <input type="number" value={round.heads_up_seconds}
                    onChange={(e) => updateRound(index, 'heads_up_seconds', parseInt(e.target.value) || 60)}
                    className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2 text-white text-sm" min="0" />
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 mb-1 font-mono">DURATION (hours)</label>
                  <input type="number" value={round.duration_seconds ? parseFloat((round.duration_seconds / 3600).toFixed(2)) : ''}
                    onChange={(e) => updateRound(index, 'duration_seconds', Math.round((parseFloat(e.target.value) || 0) * 3600))}
                    className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2 text-white text-sm" min="0" step="0.5" />
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 mb-1 font-mono">TYPE</label>
                  <div className="flex h-[38px]">
                    <button
                      onClick={() => updateRound(index, 'round_type', 'official')}
                      className={`flex-1 flex items-center justify-center gap-1 text-xs font-bold transition-colors ${
                        round.round_type === 'official'
                          ? 'bg-green-700 text-white'
                          : 'bg-zinc-800 text-zinc-500 hover:bg-zinc-700'
                      }`}
                      data-testid={`round-${round.round_number}-type-official`}
                    >
                      <Shield size={12} />Official
                    </button>
                    <button
                      onClick={() => updateRound(index, 'round_type', 'test')}
                      className={`flex-1 flex items-center justify-center gap-1 text-xs font-bold transition-colors ${
                        round.round_type === 'test'
                          ? 'bg-orange-600 text-white'
                          : 'bg-zinc-800 text-zinc-500 hover:bg-zinc-700'
                      }`}
                      data-testid={`round-${round.round_number}-type-test`}
                    >
                      <FlaskConical size={12} />Test
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 p-4 bg-blue-950/30 border border-blue-900/50 text-sm text-blue-300">
          <p className="font-bold mb-1">How auto-start works:</p>
          <p>Rounds auto-start at the scheduled date/time (UTC). The system checks every 30 seconds. You can also manually trigger any round with the "Start Now" button. Only one round can be active at a time.</p>
        </div>
      </div>

      {/* Preview Modal */}
      {previewOpen && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="preview-modal-overlay">
          <div className="bg-zinc-900 border border-zinc-700 w-full max-w-5xl max-h-[85vh] flex flex-col" data-testid="preview-modal">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-zinc-700 shrink-0">
              <div className="flex items-center gap-4">
                <Eye className="text-purple-400" size={24} />
                <h3 className="font-heading font-bold text-xl">Leaderboard Preview</h3>
                {/* League Toggle */}
                <div className="flex bg-zinc-800 p-0.5">
                  <button
                    onClick={() => switchPreviewLeague('racing')}
                    className={`px-4 py-1.5 text-sm font-bold transition-colors ${
                      previewLeague === 'racing' ? 'bg-primary text-white' : 'text-zinc-400 hover:text-white'
                    }`}
                    data-testid="preview-toggle-racing"
                  >
                    Superturismo
                  </button>
                  <button
                    onClick={() => switchPreviewLeague('moto')}
                    className={`px-4 py-1.5 text-sm font-bold transition-colors ${
                      previewLeague === 'moto' ? 'bg-secondary text-black' : 'text-zinc-400 hover:text-white'
                    }`}
                    data-testid="preview-toggle-moto"
                  >
                    MotoMSF
                  </button>
                </div>
              </div>
              <button
                onClick={() => setPreviewOpen(false)}
                className="text-zinc-400 hover:text-white transition-colors"
                data-testid="preview-close-btn"
              >
                <X size={24} />
              </button>
            </div>

            {/* Body */}
            <div className="overflow-y-auto flex-1 p-5">
              {previewLoading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
                  <span className="ml-3 text-zinc-400">Loading preview...</span>
                </div>
              ) : previewData && previewData.players.length > 0 ? (
                <div className="space-y-2">
                  {/* Header Row */}
                  <div className="grid grid-cols-12 gap-2 px-4 py-2 text-xs font-mono text-zinc-500 uppercase border-b border-zinc-700">
                    <div className="col-span-1">#</div>
                    <div className="col-span-3">Player</div>
                    <div className="col-span-2">Team</div>
                    <div className="col-span-2 text-right">Base</div>
                    <div className="col-span-2 text-right">Boosts</div>
                    <div className="col-span-2 text-right">Total</div>
                  </div>

                  {previewData.players.map((player) => (
                    <div key={player.user_id}>
                      {/* Player Row */}
                      <div
                        className={`grid grid-cols-12 gap-2 px-4 py-3 items-center cursor-pointer transition-colors ${
                          expandedPlayer === player.user_id ? 'bg-zinc-800' : 'hover:bg-zinc-800/50'
                        } ${player.rank <= 3 ? 'border-l-2 border-l-yellow-500' : ''}`}
                        onClick={() => setExpandedPlayer(expandedPlayer === player.user_id ? null : player.user_id)}
                        data-testid={`preview-player-${player.user_id}`}
                      >
                        <div className="col-span-1 font-mono font-bold text-lg">
                          {player.rank <= 3 ? (
                            <span className={player.rank === 1 ? 'text-yellow-400' : player.rank === 2 ? 'text-zinc-300' : 'text-amber-600'}>
                              {player.rank}
                            </span>
                          ) : player.rank}
                        </div>
                        <div className="col-span-3 font-medium truncate">{player.name}</div>
                        <div className="col-span-2 text-zinc-400 text-sm truncate">{player.team_name || '-'}</div>
                        <div className="col-span-2 text-right font-mono">
                          {player.total_points - player.boost_points + player.penalty_points}
                        </div>
                        <div className="col-span-2 text-right font-mono">
                          {player.boost_points > 0 && (
                            <span className="text-green-400">+{player.boost_points}</span>
                          )}
                          {player.penalty_points > 0 && (
                            <span className="text-red-400 ml-1">-{player.penalty_points}</span>
                          )}
                        </div>
                        <div className="col-span-2 text-right font-mono font-bold text-lg flex items-center justify-end gap-1">
                          {player.total_points}
                          {expandedPlayer === player.user_id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </div>
                      </div>

                      {/* Expanded Entity Breakdown */}
                      {expandedPlayer === player.user_id && (
                        <div className="bg-zinc-800/70 px-6 py-3 border-b border-zinc-700" data-testid={`preview-breakdown-${player.user_id}`}>
                          <div className="grid grid-cols-5 gap-2 text-xs font-mono text-zinc-500 mb-2 px-2">
                            <div>Entity</div>
                            <div>Type</div>
                            <div className="text-right">Base</div>
                            <div className="text-right">Boost</div>
                            <div className="text-right">Final</div>
                          </div>
                          {player.entities.map((e, idx) => (
                            <div key={e.id || `${player.user_id}-ent-${idx}`} className="grid grid-cols-5 gap-2 py-1.5 px-2 text-sm border-t border-zinc-700/50">
                              <div className="font-medium truncate">{e.name}</div>
                              <div className="text-zinc-400">{e.type}</div>
                              <div className="text-right font-mono">{e.base_pts}</div>
                              <div className="text-right font-mono">
                                {e.boost_bonus > 0 ? (
                                  <span className="text-green-400 flex items-center justify-end gap-1">
                                    <Zap size={10} />+{e.boost_bonus}
                                  </span>
                                ) : '-'}
                              </div>
                              <div className="text-right font-mono font-bold">{e.final_pts}</div>
                            </div>
                          ))}
                          {player.penalty_points > 0 && (
                            <div className="grid grid-cols-5 gap-2 py-1.5 px-2 text-sm border-t border-red-900/50 text-red-400">
                              <div className="col-span-4 text-right">Penalty</div>
                              <div className="text-right font-mono font-bold">-{player.penalty_points}</div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20 text-zinc-500">
                  <Eye size={48} className="mx-auto mb-4 opacity-30" />
                  <p>No players found for this league.</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-zinc-700 shrink-0 flex items-center justify-between text-sm text-zinc-400">
              <span>{previewData?.player_count || 0} players in {previewLeague === 'racing' ? 'Superturismo' : 'MotoMSF'}</span>
              <span className="text-zinc-500">This is a read-only preview. No data is saved.</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
